<template>
  <Dialog
    v-model:visible="dialogVisible"
    :header="t('fileSelector.title')"
    :modal="true"
    :closable="true"
    :style="{
      width: '80vw',
      maxWidth: '1000px',
      minWidth: '800px',
      height: '80vh'
    }"
    class="image-selector-dialog"
    @hide="onHide"
  >
    <div class="image-selector-content">
      <!-- 单选按钮组：灵感和我的文件 -->
      <div v-if="appId" class="mb-4 flex gap-2">
        <Button
          :label="t('fileSelector.inspiration')"
          :severity="viewMode === 'inspiration' ? undefined : 'secondary'"
          :outlined="viewMode !== 'inspiration'"
          size="small"
          :class="{ 'inspiration-active': viewMode === 'inspiration' }"
          @click="switchToInspiration"
        />
        <Button
          :label="t('fileSelector.myFiles')"
          :severity="viewMode === 'myFiles' ? undefined : 'secondary'"
          :outlined="viewMode !== 'myFiles'"
          size="small"
          :class="{ 'myfiles-active': viewMode === 'myFiles' }"
          @click="switchToMyFiles"
        />
      </div>

      <!-- 筛选和排序控件（仅在"我的文件"模式下显示） -->
      <div v-if="viewMode === 'myFiles'" class="mb-4 space-y-4">
        <!-- 第一行：主要筛选条件 -->
        <div class="flex flex-wrap gap-4 items-center">
          <!-- 文件类型筛选 -->
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium">{{
              t('fileSelector.fileType')
            }}</label>
            <Select
              v-model="filters.fileType"
              :options="fileTypeOptions"
              option-label="label"
              option-value="value"
              :placeholder="t('fileSelector.all')"
              class="w-32"
              @change="loadImages"
            />
          </div>

          <!-- 文件类别筛选 -->
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium">{{
              t('fileSelector.fileCategory')
            }}</label>
            <Select
              v-model="filters.fileCategory"
              :options="fileCategoryOptions"
              option-label="label"
              option-value="value"
              :placeholder="t('fileSelector.all')"
              class="w-32"
              @change="loadImages"
            />
          </div>

          <!-- 最近使用切换 -->
          <div class="flex items-center gap-2">
            <Checkbox
              v-model="filters.recentOnly"
              :binary="true"
              class="text-white"
              @change="onRecentOnlyChange"
            />
            <label class="text-sm">{{ t('fileSelector.recentOnly') }}</label>
          </div>
        </div>

        <!-- 第二行：排序和搜索 -->
        <div class="flex flex-wrap gap-4 items-center">
          <!-- 排序方式 -->
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium">{{
              t('fileSelector.sortBy')
            }}</label>
            <Select
              v-model="filters.sortBy"
              :options="sortOptions"
              option-label="label"
              option-value="value"
              class="w-40"
              @change="loadImages"
            />
          </div>

          <!-- 排序方向 -->
          <div class="flex items-center gap-2">
            <label class="text-sm font-medium">{{
              t('fileSelector.sortOrder')
            }}</label>
            <Select
              v-model="filters.sortOrder"
              :options="sortOrderOptions"
              option-label="label"
              option-value="value"
              class="w-28"
              @change="loadImages"
            />
          </div>

          <!-- 搜索框 -->
          <div class="flex-1 min-w-64">
            <InputText
              v-model="searchTerm"
              :placeholder="t('fileSelector.searchPlaceholder')"
              class="w-full"
              @input="onSearchInput"
            />
          </div>
        </div>
      </div>

      <!-- 文件夹导航（仅在"我的文件"模式下显示） -->
      <div v-if="viewMode === 'myFiles'" class="folder-toolbar mb-3">
        <div class="flex flex-wrap items-center gap-2 text-xs text-gray-300">
          <!-- 显示/隐藏文件夹按钮 -->
          <Button
            :icon="showFolders ? 'pi pi-eye-slash' : 'pi pi-eye'"
            :label="
              showFolders
                ? t('fileSelector.hideFolders')
                : t('fileSelector.showFolders')
            "
            size="small"
            severity="secondary"
            text
            class="folder-toggle-btn"
            @click="showFolders = !showFolders"
          />
          <template
            v-for="(crumb, index) in folderBreadcrumbs"
            :key="crumb.id ?? 'root'"
          >
            <button
              class="breadcrumb-chip"
              :class="{ active: index === folderBreadcrumbs.length - 1 }"
              type="button"
              @click="handleBreadcrumbClick(index)"
            >
              <i v-if="index === 0" class="pi pi-home mr-1"></i>
              <span>{{ crumb.name }}</span>
            </button>
            <i
              v-if="index < folderBreadcrumbs.length - 1"
              class="pi pi-angle-right text-gray-500"
            ></i>
          </template>
        </div>
      </div>

      <!-- 文件夹列表（仅在"我的文件"模式下显示） -->
      <div
        v-if="viewMode === 'myFiles' && folders.length > 0 && showFolders"
        class="folder-grid mb-4"
      >
        <div
          v-for="folder in folders"
          :key="folder.id"
          class="folder-card"
          @click="handleEnterFolder(folder)"
        >
          <div class="folder-icon-wrapper">
            <i class="pi pi-folder folder-icon"></i>
          </div>
          <div class="folder-info">
            <div class="folder-name" :title="folder.name">
              {{ folder.name }}
            </div>
            <div class="folder-meta">
              {{ (folder.file_count || 0) + ' 个文件' }}
            </div>
          </div>
        </div>
      </div>

      <!-- 加载状态 -->
      <div
        v-if="loading || initialLoading"
        class="flex justify-center items-center py-8 flex-1"
      >
        <ProgressSpinner />
      </div>

      <!-- 图片网格 -->
      <div
        v-else-if="!loading && !initialLoading"
        class="image-grid grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-4 mb-4 flex-1 overflow-y-auto"
      >
        <!-- 上传按钮（仅在"我的文件"模式下显示，且显示在第一位置） -->
        <div
          v-if="viewMode === 'myFiles'"
          class="image-item upload-item cursor-pointer border-2 rounded-lg p-2 transition-all relative flex flex-col items-center justify-center"
          :class="{
            'border-surface-200 hover:border-primary-300': !isUploading,
            'border-surface-400': isUploading
          }"
          @click="triggerFileUpload"
        >
          <input
            ref="fileInputRef"
            type="file"
            :accept="fileAcceptAttribute"
            style="display: none"
            @change="handleFileUpload"
          />
          <div
            v-if="isUploading"
            class="w-full aspect-square flex flex-col items-center justify-center"
          >
            <ProgressSpinner size="small" />
            <span class="text-xs text-center mt-2">上传中...</span>
          </div>
          <div
            v-else
            class="w-full aspect-square flex flex-col items-center justify-center"
          >
            <i class="pi pi-plus text-4xl text-primary-400 mb-2"></i>
            <span class="text-xs text-center text-primary-300">{{
              t('fileSelector.uploadFile')
            }}</span>
          </div>
        </div>
        <div
          v-for="image in images"
          :key="image.id"
          class="image-item cursor-pointer border-2 rounded-lg p-2 transition-all relative"
          :class="{
            'border-primary-500 bg-primary-50':
              selectedImage === image.filename,
            'border-surface-200 hover:border-primary-300':
              selectedImage !== image.filename
          }"
          @click="onConfirm(image)"
          @mouseenter="handleMouseEnter($event, image)"
          @mouseleave="handleMouseLeave"
        >
          <!-- 音频文件显示 -->
          <div
            v-if="isAudioFile(image.filename)"
            class="audio-item-content w-full aspect-square flex flex-col items-center justify-center rounded bg-gradient-to-br from-purple-500/20 to-pink-500/20"
          >
            <i class="pi pi-volume-up text-5xl text-purple-400 mb-2"></i>
            <span class="text-xs text-purple-300">{{
              t('fileSelector.audioFile')
            }}</span>
          </div>
          <!-- 视频文件显示 -->
          <div
            v-else-if="isVideoFile(image.filename)"
            class="video-item-content w-full aspect-square flex flex-col items-center justify-center rounded bg-gradient-to-br from-blue-500/20 to-cyan-500/20"
          >
            <i class="pi pi-play-circle text-5xl text-blue-400 mb-2"></i>
            <span class="text-xs text-blue-300">{{
              t('fileSelector.videoFile')
            }}</span>
          </div>
          <!-- 图片文件显示 -->
          <img
            v-else
            :src="getThumbnailUrl(image.url)"
            :alt="getDisplayName(image)"
            class="w-full aspect-square object-cover rounded"
            @error="handleImageError"
          />
          <div
            class="text-xs text-center mt-2 truncate"
            :title="getDisplayName(image)"
          >
            {{ getDisplayName(image) }}
          </div>
        </div>
      </div>

      <!-- 预览窗口（使用 Teleport 传送到 body，避免被裁剪） -->
      <Teleport to="body">
        <Transition name="fade">
          <div
            v-if="previewImage"
            class="image-preview-overlay"
            :style="{
              left: `${previewPosition.x}px`,
              top: `${previewPosition.y}px`
            }"
            @mouseenter="handlePreviewMouseEnter"
            @mouseleave="handlePreviewMouseLeave"
          >
            <div class="preview-content">
              <!-- 关闭按钮 -->
              <button
                type="button"
                class="preview-close-btn"
                @click="closePreview"
              >
                <i class="pi pi-times"></i>
              </button>

              <!-- 音频文件预览 -->
              <div
                v-if="isAudioFile(previewImage.filename)"
                class="preview-audio w-full h-full flex flex-col items-center justify-center rounded bg-gradient-to-br from-purple-500/20 to-pink-500/20"
              >
                <audio
                  ref="audioRef"
                  :src="previewImage.url"
                  controls
                  class="w-full max-w-sm mb-4"
                />
                <div class="text-sm text-purple-200 mt-2 text-center px-4">
                  {{ getDisplayName(previewImage) }}
                </div>
              </div>
              <!-- 视频文件预览 -->
              <div
                v-else-if="isVideoFile(previewImage.filename)"
                class="preview-video w-full h-full flex flex-col items-center justify-center rounded bg-black"
              >
                <video
                  ref="videoRef"
                  :src="previewImage.url"
                  controls
                  class="w-full h-full object-contain"
                  preload="metadata"
                />
              </div>
              <!-- 图片文件预览 -->
              <img
                v-else
                :src="getPreviewUrl(previewImage.url)"
                :alt="getDisplayName(previewImage)"
                class="preview-image w-full h-full object-contain rounded"
                @error="handleImageError"
              />
              <!-- 文件名 -->
              <div class="preview-filename">
                {{ getDisplayName(previewImage) }}
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>

      <!-- 分页控件 -->
      <div
        v-if="totalPages > 1"
        class="flex justify-center items-center gap-4 mb-4"
      >
        <Button
          icon="pi pi-angle-left"
          severity="secondary"
          size="small"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        />
        <span class="text-sm">
          {{ t('fileSelector.page') }} {{ currentPage }} 页 /
          {{ t('fileSelector.total') }} {{ totalPages }} 页 ({{
            t('fileSelector.total')
          }}
          {{ total }} {{ t('fileSelector.images') }})
        </span>
        <!-- <div class="flex items-center gap-2">
          <label class="text-sm">{{ t('fileSelector.perPage') }}</label>
          <Select
            v-model="filters.pageSize"
            :options="pageSizeOptions"
            option-label="label"
            option-value="value"
            class="w-24"
            @change="loadImages"
          />
        </div> -->
        <Button
          icon="pi pi-angle-right"
          severity="secondary"
          size="small"
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
        />
      </div>

      <!-- 操作按钮 -->
      <!-- <div class="flex justify-end gap-2">
        <Button label="取消" severity="secondary" @click="onCancel" />
        <Button label="确定" :disabled="!selectedImage" @click="onConfirm" />
      </div> -->
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import Select from 'primevue/select'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { FASTAPI_CONFIG } from '@/config/fastapi'
import { api } from '@/scripts/api'
import { useFileApiService } from '@/services/fileApiService'
import { ShareService } from '@/services/shareService'
import type { ShareResponse } from '@/types/share'

interface ImageItem {
  id: number
  filename: string
  url: string
  file_type: string
  file_category?: string
  last_used_at?: string
  created_at: string
  obs_url?: string
  original_filename?: string
  display_name?: string
  user_type?: string // uploaded, generated
  folder_id?: number | null
}

interface FileFolder {
  id: number
  name: string
  parent_id?: number | null
  file_count?: number
}

interface FolderBreadcrumb {
  id: number | null
  name: string
}

interface FileListResponse {
  files: ImageItem[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

const props = defineProps<{
  visible: boolean
  defaultFileCategory?: string | null
  appId?: number | null // 应用ID，用于获取灵感参数
  inputType?: string // 输入类型，如 'file.image'、'file.audio' 或 'file.video'
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  select: [filename: string, imageObj?: ImageItem]
}>()

// 国际化
const { t } = useI18n()

// 文件API服务
const fileApi = useFileApiService()

// 使用computed来处理visible的更新
const dialogVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit('update:visible', value)
})

// 数据状态
const images = ref<ImageItem[]>([])
const loading = ref(false)
const selectedImage = ref<string>('')
const searchTerm = ref('')
const currentPage = ref(1)
const totalPages = ref(1)
const total = ref(0)
const currentFolderId = ref<number | null>(null)
const folders = ref<FileFolder[]>([])
const folderBreadcrumbs = ref<FolderBreadcrumb[]>([
  { id: null, name: '根目录' }
])
const showFolders = ref(true) // 控制文件夹列表的显示/隐藏

// 视图模式：'inspiration' 或 'myFiles'，默认显示我的文件
const viewMode = ref<'inspiration' | 'myFiles'>('myFiles')
// 灵感参数数据
const inspirationShares = ref<ShareResponse[]>([])
const inspirationLoading = ref(false)
// 初始加载状态（用于控制首次打开时的显示）
const initialLoading = ref(true)

// 预览相关状态
const previewImage = ref<ImageItem | null>(null)
const previewPosition = ref({ x: 0, y: 0 })
const previewTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
const audioRef = ref<HTMLAudioElement | null>(null)
const videoRef = ref<HTMLVideoElement | null>(null)

// 文件上传相关状态
const fileInputRef = ref<HTMLInputElement | null>(null)
const isUploading = ref(false)

// 计算自适应的每页数量
const calculatePageSize = () => {
  // 获取窗口宽度来确定列数
  const width = window.innerWidth
  let columns = 4 // 默认 (grid-cols-4)

  if (width >= 1280) {
    columns = 10 // xl:grid-cols-10
  } else if (width >= 1024) {
    columns = 8 // lg:grid-cols-8
  } else if (width >= 640) {
    columns = 6 // sm:grid-cols-6
  }

  // 计算可视区域的行数
  // 50vh 的高度，每个图片项大约 120px (包含图片和标题)
  const viewportHeight = window.innerHeight * 0.5
  const itemHeight = 120 // 图片项高度 (含padding和标题)
  const rows = Math.floor(viewportHeight / itemHeight)

  // 计算每页数量，至少显示1行
  // 减1是因为上传按钮占据一个网格位置
  const pageSize = Math.max(columns * Math.max(rows, 1), columns * 2) - 1

  // 确保至少为1（如果有至少一行）
  return Math.max(pageSize, 1)
}

// 筛选条件
const filters = ref({
  fileType: null as string | null,
  fileCategory: null as string | null,
  sortBy: 'created_at',
  sortOrder: 'desc',
  pageSize: calculatePageSize(),
  recentOnly: false
})

// 选项配置
const fileTypeOptions = computed(() => [
  { label: t('fileSelector.all'), value: null },
  { label: t('fileSelector.input'), value: 'input' },
  { label: t('fileSelector.output'), value: 'output' }
])

const fileCategoryOptions = computed(() => [
  { label: t('fileSelector.all'), value: null },
  { label: t('fileSelector.image'), value: 'image' },
  { label: t('fileSelector.video'), value: 'video' },
  { label: t('fileSelector.audio'), value: 'audio' },
  { label: t('fileSelector.other'), value: 'other' }
])

const sortOptions = computed(() => [
  { label: t('fileSelector.createdAt'), value: 'created_at' },
  { label: t('fileSelector.lastUsed'), value: 'last_used_at' },
  { label: t('fileSelector.filename'), value: 'filename' },
  { label: t('fileSelector.fileSize'), value: 'file_size' }
])

const sortOrderOptions = computed(() => [
  { label: t('fileSelector.desc'), value: 'desc' },
  { label: t('fileSelector.asc'), value: 'asc' }
])

// 根据 inputType 生成文件选择器的 accept 属性
const fileAcceptAttribute = computed(() => {
  if (props.inputType === 'file.image') {
    return 'image/*'
  } else if (props.inputType === 'file.audio') {
    return 'audio/*'
  } else if (props.inputType === 'file.video') {
    return 'video/*'
  }
  // 默认允许所有类型
  return 'image/*,video/*,audio/*'
})

// 搜索防抖
let searchTimeout: ReturnType<typeof setTimeout> | null = null

const onSearchInput = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    currentPage.value = 1
    if (viewMode.value === 'myFiles') {
      void loadImages()
    } else {
      void loadInspirationFiles()
    }
  }, 500)
}

const onRecentOnlyChange = () => {
  // 如果勾选了最近使用，自动切换到使用时间排序
  if (filters.value.recentOnly) {
    filters.value.sortBy = 'last_used_at'
  }
  currentPage.value = 1
  if (viewMode.value === 'myFiles') {
    void loadImages()
  } else {
    void loadInspirationFiles()
  }
}

// 切换到灵感模式
const switchToInspiration = async () => {
  viewMode.value = 'inspiration'
  currentPage.value = 1
  searchTerm.value = ''
  loading.value = true
  try {
    // 如果没有加载过灵感参数，先加载
    if (inspirationShares.value.length === 0 && !inspirationLoading.value) {
      await loadInspirationShares()
    }
    await loadInspirationFiles()
  } finally {
    loading.value = false
  }
}

// 切换到我的文件模式
const switchToMyFiles = async () => {
  viewMode.value = 'myFiles'
  currentPage.value = 1
  searchTerm.value = ''
  loading.value = true
  try {
    await loadImages()
  } finally {
    loading.value = false
  }
}

// 加载灵感参数列表
const loadInspirationShares = async () => {
  if (!props.appId || inspirationLoading.value) return

  inspirationLoading.value = true
  try {
    const data = await ShareService.getShares({
      page: 1,
      page_size: 100, // 获取足够多的灵感参数
      redirect_share_id: props.appId,
      share_source_types: [4],
      order_by: 'created_at',
      order_desc: true,
      is_public: true
    })
    inspirationShares.value = data.shares || []
  } catch (error) {
    console.error('加载灵感参数失败:', error)
    inspirationShares.value = []
  } finally {
    inspirationLoading.value = false
  }
}

// 从灵感参数中提取文件（提取所有输入框中的文件，但根据 inputType 过滤显示）
const extractFilesFromInspiration = (): ImageItem[] => {
  if (!props.appId) return []

  const fileItems: ImageItem[] = []
  // 使用文件名+URL作为唯一标识，用于去重
  const seenFiles = new Set<string>()

  // 遍历所有灵感参数
  for (const share of inspirationShares.value) {
    const appInputs = share.app_inputs || {}
    const workflowInputs = share.workflow_app?.inputs || []

    // 方法1: 从 workflow_app.inputs 配置中提取文件
    if (workflowInputs && workflowInputs.length > 0) {
      for (const inputConfig of workflowInputs) {
        // 只处理文件类型的输入（图片、音频或视频）
        const inputType = inputConfig.type
        if (
          inputType !== 'file.image' &&
          inputType !== 'file.audio' &&
          inputType !== 'file.video' &&
          inputType !== 'file'
        ) {
          continue
        }

        // 从app_inputs中根据inputId读取实际值
        const inputId = inputConfig.inputId || inputConfig.id
        if (!inputId) {
          continue
        }

        // 优先从app_inputs中读取值（这是实际使用的值）
        let value = appInputs[inputId]

        // 如果app_inputs中没有，再尝试从workflow_app.inputs的value字段读取
        if (!value || value === '') {
          value = inputConfig.value
        }

        if (!value || typeof value !== 'string' || value.trim() === '') {
          continue
        }

        // 判断是否为图片、音频或视频文件
        const isImage = isImageFile(value)
        const isAudio = isAudioFile(value)
        const isVideo = isVideoFile(value)

        // 只提取图片、音频和视频文件
        if (!isImage && !isAudio && !isVideo) {
          continue
        }

        // 构建文件URL
        let fileUrl = value
        if (!value.startsWith('http://') && !value.startsWith('https://')) {
          fileUrl = `https://hao-wa-ai.obs.cn-south-1.myhuaweicloud.com/${value}`
        }

        // 使用文件名+URL作为唯一标识进行去重
        const fileKey = `${value}|${fileUrl}`
        if (seenFiles.has(fileKey)) {
          continue // 跳过重复文件
        }
        seenFiles.add(fileKey)

        // 确定文件类别
        let fileCategory: string
        if (isAudio) {
          fileCategory = 'audio'
        } else if (isVideo) {
          fileCategory = 'video'
        } else {
          fileCategory = 'image'
        }

        // 创建ImageItem对象
        const idBase = `${share.id}-${inputId}-${value}`
        let id = 0
        for (let i = 0; i < idBase.length; i++) {
          id = ((id << 5) - id + idBase.charCodeAt(i)) & 0x7fffffff
        }
        let finalId = id
        let idIndex = 0
        while (fileItems.some((item) => item.id === finalId)) {
          idIndex++
          finalId = id + idIndex
        }

        const fileItem: ImageItem = {
          id: finalId,
          filename: value,
          url: fileUrl,
          file_type: 'input',
          file_category: fileCategory,
          created_at: share.created_at || new Date().toISOString(),
          original_filename: value
        }

        fileItems.push(fileItem)
      }
    }

    // 方法2: 直接从 app_inputs 中提取所有文件类型的值
    // 检查所有以 -image、-filename、-audio 等结尾的字段
    for (const [key, value] of Object.entries(appInputs)) {
      // 跳过已经处理过的字段（这些字段已经在方法1中处理了）
      const isProcessed = workflowInputs?.some(
        (input: any) =>
          (input.inputId || input.id) === key &&
          (input.type === 'file.image' ||
            input.type === 'file.audio' ||
            input.type === 'file.video' ||
            input.type === 'file')
      )
      if (isProcessed) {
        continue
      }

      // 检查字段名是否看起来像文件字段
      const isFileField =
        key.toLowerCase().endsWith('-image') ||
        key.toLowerCase().endsWith('-filename') ||
        key.toLowerCase().endsWith('-audio') ||
        key.toLowerCase().endsWith('-video') ||
        key.toLowerCase().endsWith('-file')

      if (!isFileField) {
        continue
      }

      if (!value || typeof value !== 'string' || value.trim() === '') {
        continue
      }

      // 判断是否为图片、音频或视频文件
      const isImage = isImageFile(value)
      const isAudio = isAudioFile(value)
      const isVideo = isVideoFile(value)

      // 只提取图片、音频和视频文件
      if (!isImage && !isAudio && !isVideo) {
        continue
      }

      // 构建文件URL
      let fileUrl = value
      if (!value.startsWith('http://') && !value.startsWith('https://')) {
        fileUrl = `https://hao-wa-ai.obs.cn-south-1.myhuaweicloud.com/${value}`
      }

      // 使用文件名+URL作为唯一标识进行去重
      const fileKey = `${value}|${fileUrl}`
      if (seenFiles.has(fileKey)) {
        continue // 跳过重复文件
      }
      seenFiles.add(fileKey)

      // 确定文件类别
      let fileCategory: string
      if (isAudio) {
        fileCategory = 'audio'
      } else if (isVideo) {
        fileCategory = 'video'
      } else {
        fileCategory = 'image'
      }

      // 创建ImageItem对象
      const idBase = `${share.id}-${key}-${value}`
      let id = 0
      for (let i = 0; i < idBase.length; i++) {
        id = ((id << 5) - id + idBase.charCodeAt(i)) & 0x7fffffff
      }
      let finalId = id
      let idIndex = 0
      while (fileItems.some((item) => item.id === finalId)) {
        idIndex++
        finalId = id + idIndex
      }

      const fileItem: ImageItem = {
        id: finalId,
        filename: value,
        url: fileUrl,
        file_type: 'input',
        file_category: fileCategory,
        created_at: share.created_at || new Date().toISOString(),
        original_filename: value
      }

      fileItems.push(fileItem)
    }
  }

  console.log('提取的文件数量:', fileItems.length)

  // 根据 inputType 过滤文件类型
  if (props.inputType) {
    const filteredItems = fileItems.filter((item) => {
      if (props.inputType === 'file.image') {
        // 图片输入框：只显示图片和视频
        return item.file_category === 'image' || item.file_category === 'video'
      } else if (props.inputType === 'file.audio') {
        // 音频输入框：只显示音频
        return item.file_category === 'audio'
      } else if (props.inputType === 'file.video') {
        return item.file_category === 'video'
      }
      // 其他类型：显示所有
      return true
    })
    console.log('过滤后的文件数量:', filteredItems.length)
    return filteredItems
  }

  // 如果没有指定 inputType，返回所有文件
  return fileItems
}

// 判断是否为图片文件
const isImageFile = (filename: string): boolean => {
  if (!filename) return false
  const imageExtensions = [
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.bmp',
    '.webp',
    '.svg'
  ]
  const lowerValue = filename.toLowerCase()
  return (
    imageExtensions.some((ext) => lowerValue.endsWith(ext)) ||
    lowerValue.startsWith('http://') ||
    lowerValue.startsWith('https://')
  )
}

// 加载灵感文件
const loadInspirationFiles = async () => {
  if (!props.appId) {
    images.value = []
    return
  }

  loading.value = true
  try {
    const extractedFiles = extractFilesFromInspiration()

    // 应用搜索过滤
    let filteredFiles = extractedFiles
    if (searchTerm.value) {
      const searchLower = searchTerm.value.toLowerCase()
      filteredFiles = extractedFiles.filter(
        (file) =>
          file.filename.toLowerCase().includes(searchLower) ||
          (file.original_filename &&
            file.original_filename.toLowerCase().includes(searchLower))
      )
    }

    // 分页处理
    const pageSize = filters.value.pageSize
    const startIndex = (currentPage.value - 1) * pageSize
    const endIndex = startIndex + pageSize

    images.value = filteredFiles.slice(startIndex, endIndex)
    total.value = filteredFiles.length
    totalPages.value = Math.ceil(filteredFiles.length / pageSize)
  } catch (error) {
    console.error('加载灵感文件失败:', error)
    images.value = []
  } finally {
    loading.value = false
  }
}

const loadImages = async () => {
  if (!dialogVisible.value) return

  loading.value = true
  try {
    const params: any = {
      page: currentPage.value,
      page_size: filters.value.pageSize,
      sort_by: filters.value.sortBy,
      sort_order: filters.value.sortOrder,
      search: searchTerm.value || undefined,
      recent_only: filters.value.recentOnly,
      folder_id: currentFolderId.value !== null ? currentFolderId.value : 0
    }

    // 添加可选的筛选参数
    if (filters.value.fileType) {
      params.file_type = filters.value.fileType
    }
    if (filters.value.fileCategory) {
      params.file_category = filters.value.fileCategory
    }

    const response: FileListResponse = await fileApi.get('/list', params)

    images.value = response.files.map((file) => ({
      ...file,
      url:
        file.obs_url ||
        file.url ||
        `${FASTAPI_CONFIG.BASE_URL}/api/v1/comfyui/view?filename=${file.filename}`
    }))

    totalPages.value = response.total_pages
    total.value = response.total
  } catch (error) {
    console.error('加载图片失败:', error)
    images.value = []
  } finally {
    loading.value = false
  }
}

const loadFolders = async (parentId: number | null = currentFolderId.value) => {
  try {
    const params =
      parentId !== null && parentId !== undefined ? { parent_id: parentId } : {}
    const response: FileFolder[] = await fileApi.get('/folders', params)
    folders.value = response
  } catch (error) {
    console.error('加载文件夹失败:', error)
    folders.value = []
  }
}

const handleEnterFolder = (folder: FileFolder) => {
  currentFolderId.value = folder.id
  folderBreadcrumbs.value = [
    ...folderBreadcrumbs.value,
    { id: folder.id, name: folder.name }
  ]
  currentPage.value = 1
  void loadFolders(folder.id)
  void loadImages()
}

const handleBreadcrumbClick = (index: number) => {
  if (index < 0 || index >= folderBreadcrumbs.value.length) return
  folderBreadcrumbs.value = folderBreadcrumbs.value.slice(0, index + 1)
  const target = folderBreadcrumbs.value[index]
  currentFolderId.value = target.id
  currentPage.value = 1
  void loadFolders(target.id ?? null)
  void loadImages()
}

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
    if (viewMode.value === 'myFiles') {
      void loadImages()
    } else {
      void loadInspirationFiles()
    }
  }
}

// 处理鼠标悬停显示预览
const handleMouseEnter = (event: MouseEvent, image: ImageItem) => {
  // 清除之前的延迟
  if (previewTimeout.value) {
    clearTimeout(previewTimeout.value)
  }

  // 立即获取元素引用，避免在延迟回调中 event.currentTarget 失效
  const itemElement = event.currentTarget as HTMLElement
  if (!itemElement) {
    return
  }

  // 延迟显示预览（避免鼠标快速移动时频繁显示）
  previewTimeout.value = setTimeout(() => {
    // 再次检查元素是否存在（可能在延迟期间被移除）
    if (!itemElement || !itemElement.getBoundingClientRect) {
      return
    }

    previewImage.value = image
    // 计算预览窗口位置（相对于视口，使用 fixed 定位）
    const itemRect = itemElement.getBoundingClientRect()
    const previewWidth = 400
    const previewHeight = 400
    const spacing = 10
    const margin = 10

    // 水平位置：居中于文件项
    let x = itemRect.left + itemRect.width / 2 - previewWidth / 2

    // 确保预览窗口不超出视口水平边界
    if (x < margin) {
      x = margin
    }
    if (x + previewWidth > window.innerWidth - margin) {
      x = window.innerWidth - previewWidth - margin
    }

    // 计算上方和下方的可用空间
    const spaceAbove = itemRect.top - margin
    const spaceBelow = window.innerHeight - itemRect.bottom - margin

    // 垂直位置：优先显示在上方，如果空间不够则显示在下方
    let y: number
    if (spaceAbove >= previewHeight + spacing) {
      // 上方空间足够，显示在上方
      y = itemRect.top - previewHeight - spacing
    } else if (spaceBelow >= previewHeight + spacing) {
      // 下方空间足够，显示在下方
      y = itemRect.bottom + spacing
    } else {
      // 上下空间都不够，选择空间更大的方向
      if (spaceAbove > spaceBelow) {
        // 上方空间更大，显示在上方（可能部分超出）
        y = Math.max(margin, itemRect.top - previewHeight - spacing)
      } else {
        // 下方空间更大，显示在下方（可能部分超出）
        y = Math.min(
          window.innerHeight - previewHeight - margin,
          itemRect.bottom + spacing
        )
      }
    }

    // 最终边界检查，确保不超出视口
    if (y < margin) {
      y = margin
    }
    if (y + previewHeight > window.innerHeight - margin) {
      y = window.innerHeight - previewHeight - margin
    }

    previewPosition.value = { x, y }
  }, 300) // 300ms 延迟
}

// 预览窗口是否在鼠标悬停中
const isPreviewHovered = ref(false)

// 处理鼠标离开图片项
const handleMouseLeave = () => {
  if (previewTimeout.value) {
    clearTimeout(previewTimeout.value)
    previewTimeout.value = null
  }
  // 如果是音频文件，不自动关闭预览窗口
  if (previewImage.value && isAudioFile(previewImage.value.filename)) {
    return
  }
  // 如果预览窗口正在被悬停，也不关闭
  if (isPreviewHovered.value) {
    return
  }
  previewImage.value = null
}

// 处理鼠标进入预览窗口
const handlePreviewMouseEnter = () => {
  isPreviewHovered.value = true
}

// 处理鼠标离开预览窗口
const handlePreviewMouseLeave = () => {
  isPreviewHovered.value = false
  // 如果是音频或视频文件，不自动关闭
  if (
    previewImage.value &&
    (isAudioFile(previewImage.value.filename) ||
      isVideoFile(previewImage.value.filename))
  ) {
    return
  }
  // 延迟关闭，避免快速移动时闪烁
  setTimeout(() => {
    if (!isPreviewHovered.value) {
      previewImage.value = null
    }
  }, 100)
}

// 关闭预览窗口
const closePreview = () => {
  // 如果正在播放音频，先暂停
  if (audioRef.value) {
    audioRef.value.pause()
    audioRef.value = null
  }
  // 如果正在播放视频，先暂停
  if (videoRef.value) {
    videoRef.value.pause()
    videoRef.value = null
  }
  previewImage.value = null
  isPreviewHovered.value = false
}

const onConfirm = async (image?: ImageItem) => {
  if (image) {
    // 更新文件使用时间（仅在"我的文件"模式下，且文件有真实ID时调用）
    if (
      viewMode.value === 'myFiles' &&
      image.id &&
      typeof image.id === 'number'
    ) {
      try {
        await fileApi.updateFileUsage(image.id)
      } catch (error) {
        // 静默失败，不影响文件选择流程
        console.warn('更新文件使用时间失败:', error)
      }
    }
    // 直接选择传入的图片，发送完整的 image 对象
    emit('select', image.filename, image)
    emit('update:visible', false)
  } else if (selectedImage.value) {
    // 选择已选中的图片，查找对应的完整对象
    const selectedImageObj = images.value.find(
      (img) => img.filename === selectedImage.value
    )
    // 更新文件使用时间（仅在"我的文件"模式下，且文件有真实ID时调用）
    if (
      viewMode.value === 'myFiles' &&
      selectedImageObj?.id &&
      typeof selectedImageObj.id === 'number'
    ) {
      try {
        await fileApi.updateFileUsage(selectedImageObj.id)
      } catch (error) {
        // 静默失败，不影响文件选择流程
        console.warn('更新文件使用时间失败:', error)
      }
    }
    emit('select', selectedImage.value, selectedImageObj)
    emit('update:visible', false)
  }
}

const onHide = () => {
  selectedImage.value = ''
  searchTerm.value = ''
  currentPage.value = 1
  currentFolderId.value = null
  folders.value = []
  folderBreadcrumbs.value = [{ id: null, name: '根目录' }]
  viewMode.value = 'myFiles' // 重置为默认的我的文件模式
  inspirationShares.value = []
  images.value = []
  initialLoading.value = true
  loading.value = false
  filters.value = {
    fileType: null,
    fileCategory: null,
    sortBy: 'created_at',
    sortOrder: 'desc',
    pageSize: 20,
    recentOnly: false
  }
  emit('update:visible', false)
}

const handleImageError = (event: Event) => {
  // 图片加载失败时不做任何处理，让图片保持失败状态
  console.warn('图片加载失败:', (event.target as HTMLImageElement).src)
}

// 获取缩略图URL
const getThumbnailUrl = (url: string): string => {
  if (!url) return ''

  // 如果URL已经包含查询参数，使用&连接，否则使用?连接
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}x-image-process=image/resize,w_200`
}

// 获取预览图URL（比缩略图更大）
const getPreviewUrl = (url: string): string => {
  if (!url) return ''

  // 如果URL已经包含查询参数，使用&连接，否则使用?连接
  const separator = url.includes('?') ? '&' : '?'
  // 预览图使用更大的尺寸（800px宽度）
  return `${url}${separator}x-image-process=image/resize,w_800`
}

// 获取显示名称（与UserWorksStatsCard保持一致）
const getDisplayName = (image: ImageItem): string => {
  // 如果是上传的文件且没有display_name，显示原文件名
  if (image.user_type === 'uploaded' && !image.display_name?.trim()) {
    return image.original_filename || image.filename
  }
  // 其他情况：优先使用display_name，否则使用filename
  return image.display_name?.trim() || image.filename
}

// 判断是否为音频文件
const isAudioFile = (filename: string): boolean => {
  if (!filename) return false
  const audioExtensions = [
    '.mp3',
    '.wav',
    '.ogg',
    '.m4a',
    '.aac',
    '.flac',
    '.wma',
    '.webm'
  ]
  const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'))
  return audioExtensions.includes(ext)
}

// 判断是否为视频文件
const isVideoFile = (filename: string): boolean => {
  if (!filename) return false
  const videoExtensions = [
    '.mp4',
    '.avi',
    '.mov',
    '.wmv',
    '.flv',
    '.mkv',
    '.webm',
    '.m4v'
  ]
  const ext = filename.toLowerCase().substring(filename.lastIndexOf('.'))
  return videoExtensions.includes(ext)
}

// 触发文件上传
const triggerFileUpload = () => {
  if (isUploading.value) return
  fileInputRef.value?.click()
}

// 处理文件上传（选择图片后直接上传，上传成功后自动选中并确认）
const handleFileUpload = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  isUploading.value = true
  try {
    const formData = new FormData()
    formData.append('image', file)
    formData.append('subfolder', 'workflow-apps')
    formData.append('folder_name', '上传')

    // 使用完整URL，因为正确的接口是 /api/v1/upload/image
    const url = `${FASTAPI_CONFIG.BASE_URL}/api/v1/upload/image`
    const token = await api.getAuthToken()

    const headers: Record<string, string> = {
      'Comfy-User': api.user || ''
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    })

    if (response.ok) {
      const result = (await response.json()) as {
        name?: string
        subfolder?: string
        file_id?: number
        obs_url?: string
      }
      // 上传成功后刷新图片列表
      currentPage.value = 1
      await loadImages()
      // 清空 input 值，允许重复上传同一文件
      if (input) {
        input.value = ''
      }
      // 选择图片后直接上传，上传成功后自动选中并确认
      const uploadedFilename = result.subfolder
        ? `${result.subfolder}/${result.name}`
        : result.name || ''
      const matchedImage = images.value.find(
        (img) =>
          img.filename === uploadedFilename ||
          img.filename === result.name ||
          (result.file_id && img.id === result.file_id)
      )
      if (matchedImage) {
        await onConfirm(matchedImage)
      } else if (result.name && result.obs_url) {
        // 若列表中未找到（如分页、缓存延迟），用上传结果构造并确认
        await onConfirm({
          filename: uploadedFilename || result.name,
          url: result.obs_url,
          obs_url: result.obs_url,
          id: result.file_id,
          user_type: 'uploaded'
        } as ImageItem)
      }
    } else {
      const errorData = await response.json()
      throw new Error(errorData.detail || '上传失败')
    }
  } catch (error: any) {
    console.error('上传文件失败:', error)
    // 可以在这里添加错误提示
  } finally {
    isUploading.value = false
  }
}

// 窗口大小变化时重新计算每页数量
const handleResize = () => {
  const newPageSize = calculatePageSize()
  if (newPageSize !== filters.value.pageSize) {
    filters.value.pageSize = newPageSize
    currentPage.value = 1 // 重置到第一页
    if (props.visible) {
      void loadImages()
    }
  }
}

// 监听visible变化，重置状态并加载数据
// immediate: true 确保从 ComboWidget 等以 visible=true 挂载时也会执行加载
watch(
  () => props.visible,
  async (newVisible) => {
    if (newVisible) {
      // 重置所有状态
      selectedImage.value = ''
      searchTerm.value = ''
      currentPage.value = 1
      currentFolderId.value = null
      folders.value = []
      folderBreadcrumbs.value = [{ id: null, name: '根目录' }]
      images.value = [] // 清空图片列表，避免显示之前的内容
      initialLoading.value = true
      loading.value = true
      // 设置默认文件类别（优先使用 defaultFileCategory，否则根据 inputType 推断）
      if (props.defaultFileCategory) {
        filters.value.fileCategory = props.defaultFileCategory
      } else if (props.inputType) {
        // 根据 inputType 自动设置 file_category
        if (props.inputType === 'file.image') {
          filters.value.fileCategory = 'image'
        } else if (props.inputType === 'file.audio') {
          filters.value.fileCategory = 'audio'
        } else if (props.inputType === 'file.video') {
          filters.value.fileCategory = 'video'
        }
      }
      // 重新计算页面大小
      filters.value.pageSize = calculatePageSize()

      // 等待DOM更新后再加载数据，确保Dialog正确渲染
      await nextTick()

      // 如果有appId，检查是否有灵感参数，决定默认视图模式
      if (props.appId) {
        await checkInspirationAndSetMode()
      } else {
        viewMode.value = 'myFiles'
        try {
          await loadFolders(null)
          await loadImages()
        } finally {
          loading.value = false
          initialLoading.value = false
        }
      }
    } else {
      // 关闭时重置加载状态
      initialLoading.value = true
      images.value = []
    }
  },
  { immediate: true }
)

// 检查是否有灵感参数，并设置默认视图模式
const checkInspirationAndSetMode = async () => {
  initialLoading.value = true
  loading.value = true

  try {
    if (!props.appId) {
      viewMode.value = 'myFiles'
      await loadFolders(null)
      await loadImages()
      return
    }

    // 先加载灵感参数（供用户切换时使用）
    await loadInspirationShares()
    // 优先显示我的文件
    viewMode.value = 'myFiles'
    await loadFolders(null)
    await loadImages()
  } catch (error) {
    console.error('检查灵感参数失败:', error)
    // 出错时也默认显示我的文件
    viewMode.value = 'myFiles'
    await loadFolders(null)
    await loadImages()
  } finally {
    loading.value = false
    initialLoading.value = false
  }
}

// 组件挂载时添加窗口大小监听
onMounted(() => {
  window.addEventListener('resize', handleResize)
})

// 监听预览窗口关闭，清理音频和视频
watch(
  () => previewImage.value,
  (newValue) => {
    // 当预览窗口关闭时，清理音频和视频
    if (!newValue) {
      if (audioRef.value) {
        audioRef.value.pause()
        audioRef.value = null
      }
      if (videoRef.value) {
        videoRef.value.pause()
        videoRef.value = null
      }
    }
  }
)

// 组件卸载时移除监听器
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  // 清理预览相关的状态
  if (previewTimeout.value) {
    clearTimeout(previewTimeout.value)
    previewTimeout.value = null
  }
  // 清理音频
  if (audioRef.value) {
    audioRef.value.pause()
    audioRef.value = null
  }
  // 清理视频
  if (videoRef.value) {
    videoRef.value.pause()
    videoRef.value = null
  }
  previewImage.value = null
})
</script>

<style scoped>
.image-selector-content {
  max-height: 70vh;
  background-color: #1a1a1a;
  color: #ffffff;
}

.image-grid {
  overflow-y: auto;
}

/* 只有在内容超出时才显示滚动条 */
.image-grid::-webkit-scrollbar {
  width: 8px;
}

.image-grid::-webkit-scrollbar-track {
  background: #2d2d2d;
  border-radius: 4px;
}

.image-grid::-webkit-scrollbar-thumb {
  background: #4d4d4d;
  border-radius: 4px;
}

.image-grid::-webkit-scrollbar-thumb:hover {
  background: #666666;
}

.folder-toolbar {
  padding: 8px 0;
  border-top: 1px solid #3b2a1a;
  border-bottom: 1px solid #3b2a1a;
  margin-bottom: 12px;
}

.breadcrumb-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  border: none;
  /* background-color: rgba(249, 115, 22, 0.12); */
  background-color: #f97316;
  cursor: pointer;
  font-size: 12px;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  color: #111827;
}

/* .breadcrumb-chip.active {
  background-color: #f97316;
}

.breadcrumb-chip:not(.active):hover {
  background-color: rgba(249, 115, 22, 0.22);
} */

.folder-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 8px;
}

.folder-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background-color: #1f1f1f;
  border: 1px solid rgba(249, 115, 22, 0.35);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    transform 0.15s ease;
}

.folder-card:hover {
  background-color: #262626;
  border-color: #f97316;
  transform: translateY(-1px);
}

.folder-icon-wrapper {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
}

.folder-icon {
  color: #fdba74;
  font-size: 16px;
}

.folder-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.folder-name {
  font-size: 13px;
  /* color: #fef3c7; */
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.folder-meta {
  font-size: 11px;
  color: #9ca3af;
}

.image-item {
  transition: all 0.2s ease;
  background-color: #2d2d2d;
  border-color: #404040;
}

.image-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  background-color: #3d3d3d;
}

.upload-item {
  background-color: #2d2d2d;
  border-color: #404040;
}

.upload-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  background-color: #3d3d3d;
}

/* 黑色模式下的弹窗样式 */
:deep(.p-dialog) {
  background-color: #1a1a1a;
  color: #ffffff;
  min-width: 800px;
  width: 80vw;
  max-width: 1000px;
  height: 80vh !important;
  max-height: 80vh !important;
  display: flex;
  flex-direction: column;
}

.image-selector-dialog :deep(.p-dialog-content) {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

:deep(.p-dialog-mask) {
  display: flex;
  align-items: center;
  justify-content: center;
}

:deep(.p-dialog-header) {
  background-color: #2d2d2d;
  color: #ffffff;
  border-bottom: 1px solid #404040;
}

:deep(.p-dialog-content) {
  background-color: #1a1a1a;
  color: #ffffff;
}

:deep(.p-inputtext) {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #ffffff;
}

:deep(.p-inputtext:focus) {
  border-color: #f97316;
  box-shadow: 0 0 0 1px #f97316;
}

:deep(.p-select) {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #ffffff;
}

:deep(.p-select:focus) {
  border-color: #6366f1;
  box-shadow: 0 0 0 1px #6366f1;
}

:deep(.p-checkbox-box) {
  background-color: #2d2d2d;
  border-color: #404040;
}

:deep(.p-checkbox-box.p-highlight) {
  background-color: #f97316;
  border-color: #f97316;
}

:deep(.p-button) {
  background-color: #404040;
  border-color: #404040;
  color: #ffffff;
}

:deep(.p-button:hover) {
  background-color: #4d4d4d;
  border-color: #4d4d4d;
}

:deep(.p-button.p-button-secondary) {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #ffffff;
}

:deep(.p-button.p-button-secondary:hover) {
  background-color: #3d3d3d;
  border-color: #4d4d4d;
}

:deep(.p-button:disabled) {
  background-color: #1a1a1a;
  border-color: #2d2d2d;
  color: #666666;
}

/* 分页按钮样式 */
:deep(.p-button.p-button-sm) {
  background-color: #2d2d2d;
  border-color: #404040;
  color: #ffffff;
}

:deep(.p-button.p-button-sm:hover) {
  background-color: #3d3d3d;
  border-color: #4d4d4d;
}

:deep(.p-button.p-button-sm:disabled) {
  background-color: #1a1a1a;
  border-color: #2d2d2d;
  color: #666666;
}

/* 加载指示器 */
:deep(.p-progress-spinner-circle) {
  stroke: #6366f1;
}

/* 复选框样式 */
:deep(.p-checkbox-box) {
  background-color: #2d2d2d;
  border-color: #404040;
}

:deep(.p-checkbox-box.p-highlight) {
  background-color: #f97316;
  border-color: #f97316;
}

:deep(.p-checkbox-icon) {
  color: #ffffff;
}

/* 音频文件样式 */
.audio-item-content {
  position: relative;
  overflow: hidden;
}

.audio-item-content::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(168, 85, 247, 0.3) 0%,
    rgba(236, 72, 153, 0.3) 100%
  );
  animation: pulse-audio 2s ease-in-out infinite;
}

@keyframes pulse-audio {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.audio-item-content i {
  position: relative;
  z-index: 1;
  animation: bounce-icon 2s ease-in-out infinite;
}

@keyframes bounce-icon {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

/* 视频文件样式 */
.video-item-content {
  position: relative;
  overflow: hidden;
}

.video-item-content::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.3) 0%,
    rgba(6, 182, 212, 0.3) 100%
  );
  animation: pulse-video 2s ease-in-out infinite;
}

@keyframes pulse-video {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.video-item-content i {
  position: relative;
  z-index: 1;
}

/* 悬停时增强效果 */
.image-item:hover .audio-item-content i,
.image-item:hover .video-item-content i {
  transform: scale(1.15);
  transition: transform 0.3s ease;
}

/* 按钮选中效果 */
.inspiration-active,
.myfiles-active {
  background-color: #f97316 !important;
  border-color: #f97316 !important;
  color: #ffffff !important;
}

.inspiration-active:hover,
.myfiles-active:hover {
  background-color: #ea580c !important;
  border-color: #ea580c !important;
}

/* 选中状态的音频/视频文件 */
.image-item.border-primary-500 .audio-item-content {
  box-shadow: 0 0 20px rgba(168, 85, 247, 0.5);
}

.image-item.border-primary-500 .video-item-content {
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.5);
}

/* 按钮选中效果 */
:deep(.inspiration-active),
:deep(.myfiles-active) {
  background-color: #f97316 !important;
  border-color: #f97316 !important;
  color: #ffffff !important;
}

:deep(.inspiration-active:hover),
:deep(.myfiles-active:hover) {
  background-color: #ea580c !important;
  border-color: #ea580c !important;
}

/* 预览窗口样式 */
.image-preview-overlay {
  position: fixed;
  width: 400px;
  height: 400px;
  z-index: 9999;
  pointer-events: auto;
  animation: previewFadeIn 0.2s ease-out;
}

@keyframes previewFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.preview-content {
  width: 100%;
  height: 100%;
  background-color: #2d2d2d;
  border: 2px solid #f97316;
  border-radius: 12px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.5),
    0 10px 10px -5px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

.preview-close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10000;
  transition: all 0.2s ease;
}

.preview-close-btn:hover {
  background-color: rgba(249, 115, 22, 0.8);
  border-color: #f97316;
  transform: scale(1.1);
}

.preview-close-btn i {
  font-size: 16px;
}

.preview-image {
  flex: 1;
  background-color: #1a1a1a;
}

.preview-audio {
  flex: 1;
  background-color: #1a1a1a;
  padding: 20px;
}

.preview-audio audio {
  width: 100%;
  max-width: 100%;
}

.preview-video {
  flex: 1;
  background-color: #1a1a1a;
  padding: 0;
  position: relative;
  overflow: hidden;
}

.preview-video video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.preview-filename {
  padding: 8px 12px;
  background-color: rgba(0, 0, 0, 0.6);
  color: #ffffff;
  font-size: 12px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-top: 1px solid #404040;
}
</style>
